import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { id: "game-form" }
const _hoisted_2 = {
  key: 0,
  class: "notification errors-notification is-danger"
}
const _hoisted_3 = ["href"]

import { ref } from "vue";
import type { Option } from "vue3-select-component";
import TextArea from "./fields/text-area.vue";
import TextField from "./fields/text-field.vue";
import SingleSelect from "./fields/single-select.vue";
import NumberField from "./fields/number-field.vue";
import MultiSelect from "./fields/multi-select.vue";
import MultiSelectGeneric from "./fields/multi-select-generic.vue";
import FileSelect from "./fields/file-select.vue";
import DateField from "./fields/date-field.vue";
import VglistUtils from "../utils";
import { DirectUpload } from "@rails/activestorage";
import Turbolinks from "turbolinks";

// Helper to convert { id, name } to { value, label } for vue3-select-component
interface Props {
  name?: string;
  releaseDate?: Date;
  genres?: Array<{ id: number; name: string }>;
  engines?: Array<{ id: number; name: string }>;
  developers?: Array<{ id: number; name: string }>;
  publishers?: Array<{ id: number; name: string }>;
  platforms?: Array<{ id: number; name: string }>;
  series?: { id: number; name: string } | null;
  steamAppIds?: Array<{ id?: number; app_id: string }>;
  epicGamesStoreId?: string;
  gogId?: string;
  igdbId?: string;
  wikidataId?: number;
  pcgamingwikiId?: string;
  mobygamesId?: number;
  giantbombId?: string;
  submitPath: string;
  railsDirectUploadsPath: string;
  successPath?: string;
  cancelPath: string;
  create: boolean;
  cover?: File;
  coverBlob?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'game-form',
  props: {
    name: { default: "" },
    releaseDate: {},
    genres: { default: () => [] },
    engines: { default: () => [] },
    developers: { default: () => [] },
    publishers: { default: () => [] },
    platforms: { default: () => [] },
    series: {},
    steamAppIds: { default: () => [] },
    epicGamesStoreId: {},
    gogId: {},
    igdbId: {},
    wikidataId: {},
    pcgamingwikiId: {},
    mobygamesId: {},
    giantbombId: {},
    submitPath: {},
    railsDirectUploadsPath: {},
    successPath: {},
    cancelPath: {},
    create: { type: Boolean },
    cover: {},
    coverBlob: {}
  },
  setup(__props: any) {

function toOption(item: { id: number; name: string }): Option<number> {
  return { value: item.id, label: item.name };
}

// Helper to convert array of { id, name } to Option[]
function toOptions(items: Array<{ id: number; name: string }>): Option<number>[] {
  return items.map(toOption);
}

const props = __props;

const errors = ref<string[]>([]);

// Convert incoming { id, name } data to { value, label } format for vue3-select-component
const game = ref({
  name: props.name,
  releaseDate: props.releaseDate ? props.releaseDate.toString() : undefined,
  genres: toOptions(props.genres),
  engines: toOptions(props.engines),
  developers: toOptions(props.developers),
  publishers: toOptions(props.publishers),
  platforms: toOptions(props.platforms),
  series: props.series ? toOption(props.series) : null,
  steamAppIds: props.steamAppIds.map((s) => ({
    value: s.app_id,
    label: s.app_id
  })) as Option<string>[],
  epicGamesStoreId: props.epicGamesStoreId,
  gogId: props.gogId,
  igdbId: props.igdbId,
  wikidataId: props.wikidataId?.toString(),
  pcgamingwikiId: props.pcgamingwikiId,
  mobygamesId: props.mobygamesId?.toString(),
  giantbombId: props.giantbombId,
  cover: props.cover,
  coverBlob: props.coverBlob
});

const formData = {
  class: "game",
  cover: {
    label: "Cover"
  },
  name: {
    label: "Game title",
    attribute: "name"
  },
  releaseDate: {
    label: "Release Date",
    attribute: "release_date"
  },
  genres: {
    label: "Genres"
  },
  engines: {
    label: "Engines"
  },
  developers: {
    label: "Developers"
  },
  publishers: {
    label: "Publishers"
  },
  platforms: {
    label: "Platforms"
  },
  series: {
    label: "Series"
  },
  steamAppIds: {
    label: "Steam Application IDs"
  },
  epicGamesStoreId: {
    label: "Epic Games Store ID",
    attribute: "epic_games_store_id"
  },
  gogId: {
    label: "GOG.com ID",
    attribute: "gog_id"
  },
  igdbId: {
    label: "IGDB ID",
    attribute: "igdb_id"
  },
  wikidataId: {
    label: "Wikidata ID",
    attribute: "wikidata_id"
  },
  pcgamingwikiId: {
    label: "PCGamingWiki ID",
    attribute: "pcgamingwiki_id"
  },
  mobygamesId: {
    label: "MobyGames ID",
    attribute: "mobygames_id"
  },
  giantbombId: {
    label: "Giant Bomb ID",
    attribute: "giantbomb_id"
  }
};

function onChange(file: File) {
  uploadFile(file);
}

function uploadFile(file: File) {
  const url = props.railsDirectUploadsPath;
  const upload = new DirectUpload(file, url, {
    directUploadWillStoreFileWithXHR: (xhr) => {
      // Use this workaround to make sure that Direct Upload-ed images are
      // uploaded with the correct header. Otherwise they will end up being
      // private files.
      xhr.setRequestHeader("x-amz-acl", "public-read");
    }
  });

  upload.create((error, blob) => {
    if (error) {
      // TODO: Handle this error.
      console.log(error);
    } else if (blob) {
      game.value.coverBlob = blob.signed_id;
    }
  });
}

function onSubmit() {
  // Extract IDs from Option<number> format (value property contains the ID)
  const genreIds = game.value.genres.map((g) => g.value);
  const engineIds = game.value.engines.map((e) => e.value);
  const developerIds = game.value.developers.map((d) => d.value);
  const publisherIds = game.value.publishers.map((p) => p.value);
  const platformIds = game.value.platforms.map((p) => p.value);

  // Handle Steam App IDs - compare current vs original to detect deletions
  const steamAppIds: Array<{ id?: number; app_id: string; _destroy?: boolean }> = [];
  const currentAppIds = game.value.steamAppIds.map((s) => s.value);

  // Add current steam app IDs
  game.value.steamAppIds.forEach((option) => {
    const originalRecord = props.steamAppIds.find((s) => s.app_id === option.value);
    if (originalRecord?.id !== undefined) {
      steamAppIds.push({ id: originalRecord.id, app_id: option.value });
    } else {
      steamAppIds.push({ app_id: option.value });
    }
  });

  // Mark removed steam app IDs for destruction
  props.steamAppIds.forEach((original) => {
    if (!currentAppIds.includes(original.app_id) && original.id !== undefined) {
      steamAppIds.push({ id: original.id, app_id: original.app_id, _destroy: true });
    }
  });

  const submittableData: Record<string, any> = {
    game: {
      name: game.value.name,
      release_date: game.value.releaseDate,
      genre_ids: genreIds,
      engine_ids: engineIds,
      developer_ids: developerIds,
      publisher_ids: publisherIds,
      platform_ids: platformIds,
      steam_app_ids_attributes: steamAppIds,
      epic_games_store_id: game.value.epicGamesStoreId,
      gog_id: game.value.gogId,
      igdb_id: game.value.igdbId,
      wikidata_id: game.value.wikidataId,
      pcgamingwiki_id: game.value.pcgamingwikiId,
      mobygames_id: game.value.mobygamesId,
      giantbomb_id: game.value.giantbombId
    }
  };

  // If the attribute's value is an empty string, replace it with null so
  // it's nullified when sent to the backend.
  [
    "epic_games_store_id",
    "gog_id",
    "igdb_id",
    "wikidata_id",
    "pcgamingwiki_id",
    "mobygames_id",
    "giantbomb_id"
  ].forEach((attr) => {
    if (submittableData.game[attr] === "") {
      submittableData.game[attr] = null;
    }
  });

  // Extract series ID from Option format (value property contains the ID)
  if (game.value.series) {
    submittableData.game.series_id = game.value.series.value;
  } else {
    submittableData.game.series_id = null;
  }

  if (game.value.coverBlob) {
    submittableData.game.cover = game.value.coverBlob;
  }

  VglistUtils.authenticatedFetch(
    props.submitPath,
    props.create ? "POST" : "PUT",
    JSON.stringify(submittableData)
  )
    .then((gameResponse: any) => {
      if (props.create) {
        Turbolinks.visit(`${window.location.origin}/games/${gameResponse.id}`);
      } else {
        Turbolinks.visit(props.successPath || "/");
      }
    })
    .catch((errorsResp) => {
      errors.value = errorsResp;
      const submitButton = document.querySelector(".js-submit-button");
      if (submitButton) {
        submitButton.classList.add("js-submit-button-error");
        setTimeout(() => {
          submitButton.classList.remove("js-submit-button-error");
        }, 2000);
      }
    });
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (errors.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("p", null, _toDisplayString(errors.value.length > 1 ? "Errors" : "An error") + " prevented this game from being saved:", 1),
          _createElementVNode("ul", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (error) => {
              return (_openBlock(), _createElementBlock("li", { key: error }, _toDisplayString(error), 1))
            }), 128))
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(FileSelect, {
      label: formData.cover.label,
      modelValue: game.value.cover,
      "onUpdate:modelValue": [
        _cache[0] || (_cache[0] = ($event: any) => ((game.value.cover) = $event)),
        onChange
      ]
    }, null, 8, ["label", "modelValue"]),
    _createVNode(TextField, {
      "form-class": formData.class,
      attribute: formData.name.attribute,
      label: formData.name.label,
      required: true,
      modelValue: game.value.name,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((game.value.name) = $event))
    }, null, 8, ["form-class", "attribute", "label", "modelValue"]),
    _createVNode(DateField, {
      "form-class": formData.class,
      attribute: formData.releaseDate.attribute,
      label: formData.releaseDate.label,
      required: false,
      modelValue: game.value.releaseDate,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((game.value.releaseDate) = $event))
    }, null, 8, ["form-class", "attribute", "label", "modelValue"]),
    _createVNode(MultiSelect, {
      label: formData.genres.label,
      modelValue: game.value.genres,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((game.value.genres) = $event)),
      "search-path-identifier": 'genres'
    }, null, 8, ["label", "modelValue"]),
    _createVNode(MultiSelect, {
      label: formData.engines.label,
      modelValue: game.value.engines,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((game.value.engines) = $event)),
      "search-path-identifier": 'engines'
    }, null, 8, ["label", "modelValue"]),
    _createVNode(MultiSelect, {
      label: formData.developers.label,
      modelValue: game.value.developers,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((game.value.developers) = $event)),
      "search-path-identifier": 'companies'
    }, null, 8, ["label", "modelValue"]),
    _createVNode(MultiSelect, {
      label: formData.publishers.label,
      modelValue: game.value.publishers,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((game.value.publishers) = $event)),
      "search-path-identifier": 'companies'
    }, null, 8, ["label", "modelValue"]),
    _createVNode(MultiSelect, {
      label: formData.platforms.label,
      modelValue: game.value.platforms,
      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((game.value.platforms) = $event)),
      "search-path-identifier": 'platforms'
    }, null, 8, ["label", "modelValue"]),
    _createVNode(SingleSelect, {
      label: formData.series.label,
      modelValue: game.value.series,
      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((game.value.series) = $event)),
      "search-path-identifier": 'series'
    }, null, 8, ["label", "modelValue"]),
    _createVNode(NumberField, {
      "form-class": formData.class,
      attribute: formData.wikidataId.attribute,
      label: formData.wikidataId.label,
      modelValue: game.value.wikidataId,
      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((game.value.wikidataId) = $event))
    }, null, 8, ["form-class", "attribute", "label", "modelValue"]),
    _createVNode(MultiSelectGeneric, {
      label: formData.steamAppIds.label,
      modelValue: game.value.steamAppIds,
      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((game.value.steamAppIds) = $event))
    }, null, 8, ["label", "modelValue"]),
    _createVNode(TextField, {
      "form-class": formData.class,
      attribute: formData.epicGamesStoreId.attribute,
      label: formData.epicGamesStoreId.label,
      modelValue: game.value.epicGamesStoreId,
      "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((game.value.epicGamesStoreId) = $event))
    }, null, 8, ["form-class", "attribute", "label", "modelValue"]),
    _createVNode(TextField, {
      "form-class": formData.class,
      attribute: formData.gogId.attribute,
      label: formData.gogId.label,
      modelValue: game.value.gogId,
      "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((game.value.gogId) = $event))
    }, null, 8, ["form-class", "attribute", "label", "modelValue"]),
    _createVNode(TextField, {
      "form-class": formData.class,
      attribute: formData.igdbId.attribute,
      label: formData.igdbId.label,
      modelValue: game.value.igdbId,
      "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((game.value.igdbId) = $event))
    }, null, 8, ["form-class", "attribute", "label", "modelValue"]),
    _createVNode(TextField, {
      "form-class": formData.class,
      attribute: formData.pcgamingwikiId.attribute,
      label: formData.pcgamingwikiId.label,
      modelValue: game.value.pcgamingwikiId,
      "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((game.value.pcgamingwikiId) = $event))
    }, null, 8, ["form-class", "attribute", "label", "modelValue"]),
    _createVNode(TextField, {
      "form-class": formData.class,
      attribute: formData.mobygamesId.attribute,
      label: formData.mobygamesId.label,
      modelValue: game.value.mobygamesId,
      "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((game.value.mobygamesId) = $event))
    }, null, 8, ["form-class", "attribute", "label", "modelValue"]),
    _createVNode(TextField, {
      "form-class": formData.class,
      attribute: formData.giantbombId.attribute,
      label: formData.giantbombId.label,
      modelValue: game.value.giantbombId,
      "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((game.value.giantbombId) = $event))
    }, null, 8, ["form-class", "attribute", "label", "modelValue"]),
    _createElementVNode("button", {
      class: "button is-primary mr-10 mr-0-mobile is-fullwidth-mobile js-submit-button",
      value: "Submit",
      onClick: _withModifiers(onSubmit, ["prevent"])
    }, " Submit "),
    _createElementVNode("a", {
      class: "button is-fullwidth-mobile mt-5-mobile",
      href: __props.cancelPath
    }, "Cancel", 8, _hoisted_3)
  ]))
}
}

})